import { useEffect, useRef } from "react";

export default function AnimatedBackground({ type = "cosmic_nebula", customUrl = "", className = "" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (customUrl || type === "none" || !type) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize);

    // 1. MATRIX RAIN
    if (type === "matrix_rain" || type === "matrix_code") {
      const fontSize = 14;
      const columns = Math.floor(width / fontSize);
      const drops = Array.from({ length: columns }).map(() => Math.floor(Math.random() * -50));
      const chars = "INDUNGI0123456789ABCDEF$#@%*+-/<>[]{}_=~";

      const renderMatrix = () => {
        ctx.fillStyle = "rgba(4, 7, 12, 0.12)";
        ctx.fillRect(0, 0, width, height);
        ctx.font = `${fontSize}px monospace`;

        for (let i = 0; i < drops.length; i++) {
          const char = chars[Math.floor(Math.random() * chars.length)];
          const x = i * fontSize;
          const y = drops[i] * fontSize;

          ctx.fillStyle = Math.random() > 0.85 ? "#ffffff" : "#00ff66";
          ctx.fillText(char, x, y);

          if (y > height && Math.random() > 0.975) {
            drops[i] = 0;
          }
          drops[i]++;
        }
        animId = requestAnimationFrame(renderMatrix);
      };
      renderMatrix();
    }

    // 2. CS2 INFERNO & FIRE EMBERS
    else if (type === "cs_smoke_fire" || type === "cassette_tapes") {
      const emberCount = 100;
      const embers = Array.from({ length: emberCount }).map(() => ({
        x: Math.random() * width,
        y: Math.random() * height,
        radius: Math.random() * 2.5 + 0.8,
        vx: (Math.random() - 0.5) * 1.2,
        vy: -(Math.random() * 2 + 1),
        alpha: Math.random() * 0.8 + 0.2,
        color: Math.random() > 0.4 ? "#ff4500" : Math.random() > 0.5 ? "#ffa500" : "#ff1100"
      }));

      const renderFire = () => {
        ctx.fillStyle = "rgba(6, 8, 14, 0.25)";
        ctx.fillRect(0, 0, width, height);

        embers.forEach((p) => {
          ctx.save();
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
          ctx.shadowBlur = 12;
          ctx.shadowColor = p.color;
          ctx.fillStyle = p.color;
          ctx.globalAlpha = p.alpha;
          ctx.fill();
          ctx.restore();

          p.x += p.vx;
          p.y += p.vy;
          p.alpha -= 0.003;

          if (p.y < 0 || p.alpha <= 0) {
            p.x = Math.random() * width;
            p.y = height + 10;
            p.alpha = Math.random() * 0.8 + 0.2;
            p.vy = -(Math.random() * 2 + 1);
          }
        });
        animId = requestAnimationFrame(renderFire);
      };
      renderFire();
    }

    // 3. MOLTEN GOLD DUST
    else if (type === "molten_gold" || type === "cyber_girl") {
      const goldCount = 90;
      const goldParticles = Array.from({ length: goldCount }).map(() => ({
        x: Math.random() * width,
        y: Math.random() * height,
        size: Math.random() * 3 + 1,
        vx: (Math.random() - 0.5) * 0.6,
        vy: -(Math.random() * 0.8 + 0.4),
        alpha: Math.random() * 0.9 + 0.1,
        pulsing: Math.random() * 0.04
      }));

      const renderGold = () => {
        ctx.fillStyle = "rgba(7, 9, 15, 0.2)";
        ctx.fillRect(0, 0, width, height);

        goldParticles.forEach((p) => {
          ctx.save();
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.shadowBlur = 15;
          ctx.shadowColor = "#ffd700";
          ctx.fillStyle = "#ffd700";
          ctx.globalAlpha = p.alpha;
          ctx.fill();
          ctx.restore();

          p.x += p.vx;
          p.y += p.vy;
          p.alpha += Math.sin(p.pulsing) * 0.01;

          if (p.y < 0) {
            p.y = height + 5;
            p.x = Math.random() * width;
          }
        });
        animId = requestAnimationFrame(renderGold);
      };
      renderGold();
    }

    // 4. COSMIC LASER NEBULA
    else if (type === "cosmic_nebula" || type === "anime_girls") {
      const starCount = 130;
      const stars = Array.from({ length: starCount }).map(() => ({
        x: Math.random() * width,
        y: Math.random() * height,
        radius: Math.random() * 2 + 0.5,
        color: Math.random() > 0.5 ? "#00e5ff" : Math.random() > 0.5 ? "#8b5cf6" : "#f59e0b",
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        pulse: Math.random() * Math.PI
      }));

      let time = 0;
      const renderCosmic = () => {
        time += 0.01;
        ctx.fillStyle = "rgba(5, 7, 12, 0.2)";
        ctx.fillRect(0, 0, width, height);

        const grad = ctx.createRadialGradient(
          width * 0.5 + Math.sin(time) * 100,
          height * 0.4 + Math.cos(time) * 60,
          20,
          width * 0.5,
          height * 0.4,
          width * 0.6
        );
        grad.addColorStop(0, "rgba(139, 92, 246, 0.08)");
        grad.addColorStop(0.5, "rgba(0, 229, 255, 0.04)");
        grad.addColorStop(1, "rgba(0, 0, 0, 0)");
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, width, height);

        stars.forEach((s) => {
          s.pulse += 0.03;
          const currentRadius = s.radius * (0.8 + Math.sin(s.pulse) * 0.4);

          ctx.save();
          ctx.beginPath();
          ctx.arc(s.x, s.y, Math.max(0.5, currentRadius), 0, Math.PI * 2);
          ctx.shadowBlur = 10;
          ctx.shadowColor = s.color;
          ctx.fillStyle = s.color;
          ctx.globalAlpha = 0.6 + Math.sin(s.pulse) * 0.3;
          ctx.fill();
          ctx.restore();

          s.x += s.vx;
          s.y += s.vy;
          if (s.x < 0) s.x = width;
          if (s.x > width) s.x = 0;
          if (s.y < 0) s.y = height;
          if (s.y > height) s.y = 0;
        });

        animId = requestAnimationFrame(renderCosmic);
      };
      renderCosmic();
    }

    // 5. CYBERPUNK 3D HORIZON
    else if (type === "cyber_neon_city" || type === "neon_city") {
      let offset = 0;
      const renderCyberCity = () => {
        offset = (offset + 1) % 40;
        ctx.fillStyle = "rgba(4, 6, 12, 0.3)";
        ctx.fillRect(0, 0, width, height);

        const horizonY = height * 0.65;
        ctx.strokeStyle = "rgba(0, 229, 255, 0.4)";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(0, horizonY);
        ctx.lineTo(width, horizonY);
        ctx.stroke();

        ctx.strokeStyle = "rgba(0, 229, 255, 0.15)";
        ctx.lineWidth = 1;
        for (let x = -width; x < width * 2; x += 60) {
          ctx.beginPath();
          ctx.moveTo(width / 2, horizonY);
          ctx.lineTo(x, height);
          ctx.stroke();
        }

        for (let y = horizonY; y < height; y += 15) {
          const lineY = y + (offset * ((y - horizonY) / (height - horizonY)));
          if (lineY <= height) {
            ctx.beginPath();
            ctx.moveTo(0, lineY);
            ctx.lineTo(width, lineY);
            ctx.stroke();
          }
        }

        animId = requestAnimationFrame(renderCyberCity);
      };
      renderCyberCity();
    }

    // 6. TACTICAL CYBER TUNNEL
    else if (type === "cyber_tunnel" || type === "cs_swat") {
      const lines = Array.from({ length: 70 }).map(() => ({
        x: Math.random() * width,
        y: Math.random() * height,
        len: Math.random() * 40 + 10,
        speed: Math.random() * 4 + 2,
        color: Math.random() > 0.5 ? "#00e5ff" : "#ff7a1a"
      }));

      const renderTunnel = () => {
        ctx.fillStyle = "rgba(4, 6, 10, 0.25)";
        ctx.fillRect(0, 0, width, height);

        lines.forEach((l) => {
          ctx.save();
          ctx.strokeStyle = l.color;
          ctx.lineWidth = 1.5;
          ctx.shadowBlur = 8;
          ctx.shadowColor = l.color;
          ctx.beginPath();
          ctx.moveTo(l.x, l.y);
          ctx.lineTo(l.x + l.len, l.y);
          ctx.stroke();
          ctx.restore();

          l.x += l.speed;
          if (l.x > width) {
            l.x = -l.len;
            l.y = Math.random() * height;
          }
        });
        animId = requestAnimationFrame(renderTunnel);
      };
      renderTunnel();
    }

    // 7. ELECTRIC LIGHTNING STORM
    else if (type === "electric_storm") {
      let strikes = [];
      const createStrike = () => {
        const startX = Math.random() * width;
        let currX = startX;
        let currY = 0;
        const segments = [{ x: currX, y: currY }];
        while (currY < height) {
          currX += (Math.random() - 0.5) * 45;
          currY += Math.random() * 30 + 15;
          segments.push({ x: currX, y: currY });
        }
        return { segments, alpha: 1, color: Math.random() > 0.5 ? "#00e5ff" : "#a855f7" };
      };

      const renderStorm = () => {
        ctx.fillStyle = "rgba(5, 7, 14, 0.3)";
        ctx.fillRect(0, 0, width, height);

        if (Math.random() > 0.94) {
          strikes.push(createStrike());
        }

        strikes.forEach((s, idx) => {
          ctx.save();
          ctx.strokeStyle = s.color;
          ctx.lineWidth = 2;
          ctx.shadowBlur = 18;
          ctx.shadowColor = s.color;
          ctx.globalAlpha = s.alpha;
          ctx.beginPath();
          s.segments.forEach((pt, i) => {
            if (i === 0) ctx.moveTo(pt.x, pt.y);
            else ctx.lineTo(pt.x, pt.y);
          });
          ctx.stroke();
          ctx.restore();
          s.alpha -= 0.08;
          if (s.alpha <= 0) strikes.splice(idx, 1);
        });

        animId = requestAnimationFrame(renderStorm);
      };
      renderStorm();
    }

    // 8. BLOOD CS 1.6 VORTEX
    else if (type === "blood_vortex") {
      let angle = 0;
      const bloodPoints = Array.from({ length: 80 }).map(() => ({
        distance: Math.random() * (width * 0.4) + 20,
        speed: (Math.random() * 0.02 + 0.008),
        size: Math.random() * 3 + 1,
        color: Math.random() > 0.4 ? "#dc2626" : "#7f1d1d"
      }));

      const renderBlood = () => {
        angle += 0.01;
        ctx.fillStyle = "rgba(10, 4, 6, 0.25)";
        ctx.fillRect(0, 0, width, height);

        const cx = width / 2;
        const cy = height / 2;

        bloodPoints.forEach((p) => {
          p.distance -= 0.5;
          if (p.distance <= 10) p.distance = width * 0.4;
          const x = cx + Math.cos(angle * p.speed * 100) * p.distance;
          const y = cy + Math.sin(angle * p.speed * 100) * p.distance;

          ctx.save();
          ctx.beginPath();
          ctx.arc(x, y, p.size, 0, Math.PI * 2);
          ctx.shadowBlur = 15;
          ctx.shadowColor = p.color;
          ctx.fillStyle = p.color;
          ctx.fill();
          ctx.restore();
        });
        animId = requestAnimationFrame(renderBlood);
      };
      renderBlood();
    }

    // 9. DIAMOND HOLOGRAPHIC PRISM
    else if (type === "diamond_prism") {
      let phase = 0;
      const shards = Array.from({ length: 60 }).map(() => ({
        x: Math.random() * width,
        y: Math.random() * height,
        size: Math.random() * 8 + 4,
        rot: Math.random() * Math.PI,
        vRot: (Math.random() - 0.5) * 0.04,
        hue: Math.random() * 360
      }));

      const renderDiamond = () => {
        phase += 0.01;
        ctx.fillStyle = "rgba(7, 8, 18, 0.25)";
        ctx.fillRect(0, 0, width, height);

        shards.forEach((s) => {
          s.rot += s.vRot;
          const currentHue = (s.hue + phase * 40) % 360;
          ctx.save();
          ctx.translate(s.x, s.y);
          ctx.rotate(s.rot);
          ctx.strokeStyle = `hsl(${currentHue}, 90%, 65%)`;
          ctx.shadowBlur = 14;
          ctx.shadowColor = `hsl(${currentHue}, 90%, 65%)`;
          ctx.lineWidth = 1.5;
          ctx.strokeRect(-s.size / 2, -s.size / 2, s.size, s.size);
          ctx.restore();
        });
        animId = requestAnimationFrame(renderDiamond);
      };
      renderDiamond();
    }

    // 10. ARCTIC BLIZZARD SNOW
    else if (type === "arctic_blizzard") {
      const flakes = Array.from({ length: 110 }).map(() => ({
        x: Math.random() * width,
        y: Math.random() * height,
        radius: Math.random() * 2.5 + 0.8,
        vx: -(Math.random() * 1.5 + 0.8),
        vy: Math.random() * 1.8 + 1,
        alpha: Math.random() * 0.8 + 0.2
      }));

      const renderSnow = () => {
        ctx.fillStyle = "rgba(3, 8, 16, 0.25)";
        ctx.fillRect(0, 0, width, height);

        flakes.forEach((f) => {
          ctx.save();
          ctx.beginPath();
          ctx.arc(f.x, f.y, f.radius, 0, Math.PI * 2);
          ctx.shadowBlur = 10;
          ctx.shadowColor = "#38bdf8";
          ctx.fillStyle = "#e0f2fe";
          ctx.globalAlpha = f.alpha;
          ctx.fill();
          ctx.restore();

          f.x += f.vx;
          f.y += f.vy;

          if (f.y > height) { f.y = 0; f.x = Math.random() * width; }
          if (f.x < 0) { f.x = width; }
        });
        animId = requestAnimationFrame(renderSnow);
      };
      renderSnow();
    }

    // 11. VOID QUANTUM TUNNEL
    else if (type === "void_hyperspace") {
      const pCount = 90;
      let time = 0;
      const particles = Array.from({ length: pCount }).map(() => ({
        angle: Math.random() * Math.PI * 2,
        dist: Math.random() * (width * 0.5),
        speed: Math.random() * 2 + 1,
        color: Math.random() > 0.5 ? "#8b5cf6" : "#ec4899"
      }));

      const renderVoid = () => {
        time += 0.02;
        ctx.fillStyle = "rgba(8, 4, 16, 0.22)";
        ctx.fillRect(0, 0, width, height);

        const cx = width / 2;
        const cy = height / 2;

        particles.forEach((p) => {
          p.dist += p.speed;
          if (p.dist > width * 0.6) p.dist = 10;
          const x = cx + Math.cos(p.angle + time) * p.dist;
          const y = cy + Math.sin(p.angle + time) * p.dist;

          ctx.save();
          ctx.beginPath();
          ctx.arc(x, y, Math.max(1, p.dist * 0.005), 0, Math.PI * 2);
          ctx.shadowBlur = 12;
          ctx.shadowColor = p.color;
          ctx.fillStyle = p.color;
          ctx.fill();
          ctx.restore();
        });
        animId = requestAnimationFrame(renderVoid);
      };
      renderVoid();
    }

    // 12. RETROWAVE SUNSET GRID
    else if (type === "retrowave_sunset") {
      let wave = 0;
      const renderRetrowave = () => {
        wave += 0.03;
        ctx.fillStyle = "rgba(10, 4, 16, 0.25)";
        ctx.fillRect(0, 0, width, height);

        // Sunset Glow
        const sunGrad = ctx.createRadialGradient(width / 2, height * 0.55, 10, width / 2, height * 0.55, 120);
        sunGrad.addColorStop(0, "rgba(255, 0, 128, 0.4)");
        sunGrad.addColorStop(1, "rgba(0, 0, 0, 0)");
        ctx.fillStyle = sunGrad;
        ctx.fillRect(0, 0, width, height);

        // Visualizer Waves
        ctx.strokeStyle = "#f43f5e";
        ctx.lineWidth = 2;
        ctx.shadowBlur = 10;
        ctx.shadowColor = "#f43f5e";
        ctx.beginPath();
        for (let x = 0; x < width; x += 10) {
          const y = height * 0.65 + Math.sin(x * 0.02 + wave) * 15;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
        animId = requestAnimationFrame(renderRetrowave);
      };
      renderRetrowave();
    }

    // 13. TOXIC BIOHAZARD VAPOR
    else if (type === "toxic_biohazard") {
      const toxicEmbers = Array.from({ length: 80 }).map(() => ({
        x: Math.random() * width,
        y: Math.random() * height,
        size: Math.random() * 4 + 1,
        vx: (Math.random() - 0.5) * 0.8,
        vy: -(Math.random() * 1.5 + 0.5),
        color: Math.random() > 0.5 ? "#22c55e" : "#84cc16"
      }));

      const renderToxic = () => {
        ctx.fillStyle = "rgba(4, 12, 6, 0.22)";
        ctx.fillRect(0, 0, width, height);

        toxicEmbers.forEach((t) => {
          ctx.save();
          ctx.beginPath();
          ctx.arc(t.x, t.y, t.size, 0, Math.PI * 2);
          ctx.shadowBlur = 14;
          ctx.shadowColor = t.color;
          ctx.fillStyle = t.color;
          ctx.fill();
          ctx.restore();

          t.x += t.vx;
          t.y += t.vy;
          if (t.y < 0) { t.y = height + 10; t.x = Math.random() * width; }
        });
        animId = requestAnimationFrame(renderToxic);
      };
      renderToxic();
    }

    // 14. ROYAL CELESTIAL AURORA
    else if (type === "celestial_aurora") {
      let t = 0;
      const renderAurora = () => {
        t += 0.015;
        ctx.fillStyle = "rgba(3, 6, 14, 0.2)";
        ctx.fillRect(0, 0, width, height);

        for (let i = 0; i < 3; i++) {
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(0, height * 0.3 + i * 50);
          for (let x = 0; x <= width; x += 30) {
            const y = height * 0.3 + i * 50 + Math.sin(x * 0.003 + t + i) * 60 + Math.cos(x * 0.006 - t) * 30;
            ctx.lineTo(x, y);
          }
          ctx.lineTo(width, height);
          ctx.lineTo(0, height);
          ctx.closePath();

          const auroraGrad = ctx.createLinearGradient(0, height * 0.2, 0, height * 0.8);
          if (i === 0) {
            auroraGrad.addColorStop(0, "rgba(16, 185, 129, 0.15)");
            auroraGrad.addColorStop(1, "rgba(0, 0, 0, 0)");
          } else {
            auroraGrad.addColorStop(0, "rgba(168, 85, 247, 0.12)");
            auroraGrad.addColorStop(1, "rgba(0, 0, 0, 0)");
          }
          ctx.fillStyle = auroraGrad;
          ctx.fill();
          ctx.restore();
        }
        animId = requestAnimationFrame(renderAurora);
      };
      renderAurora();
    }

    // 15. MYSTIC PISCES KOI FISH & OCEANIC WATER
    else if (type === "bundle_pisces_koi" || type === "pisces_koi") {
      let koiAngle = 0;
      const bubbles = Array.from({ length: 50 }).map(() => ({
        x: Math.random() * width,
        y: Math.random() * height,
        r: Math.random() * 3 + 1,
        vy: -(Math.random() * 1.5 + 0.5),
        alpha: Math.random() * 0.7 + 0.3
      }));

      const renderKoi = () => {
        koiAngle += 0.02;
        ctx.fillStyle = "rgba(4, 10, 24, 0.25)";
        ctx.fillRect(0, 0, width, height);

        // Water ripple bubbles
        bubbles.forEach((b) => {
          ctx.save();
          ctx.beginPath();
          ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(56, 189, 248, 0.6)";
          ctx.shadowBlur = 8;
          ctx.shadowColor = "#38bdf8";
          ctx.globalAlpha = b.alpha;
          ctx.fill();
          ctx.restore();

          b.y += b.vy;
          if (b.y < 0) { b.y = height + 10; b.x = Math.random() * width; }
        });

        // 2 Swimming Glowing Ethereal Koi Fish
        const cx = width / 2;
        const cy = height / 2;
        const radiusX = Math.min(width, height) * 0.32;
        const radiusY = Math.min(width, height) * 0.22;

        [0, Math.PI].forEach((offsetAngle, i) => {
          const a = koiAngle + offsetAngle;
          const kx = cx + Math.cos(a) * radiusX;
          const ky = cy + Math.sin(a * 2) * radiusY * 0.6;
          const fishColor = i === 0 ? "#38bdf8" : "#818cf8";

          ctx.save();
          ctx.translate(kx, ky);
          ctx.rotate(a + Math.PI / 2);

          // Glowing Koi Body
          ctx.beginPath();
          ctx.ellipse(0, 0, 18, 7, 0, 0, Math.PI * 2);
          ctx.fillStyle = fishColor;
          ctx.shadowBlur = 20;
          ctx.shadowColor = fishColor;
          ctx.fill();

          // Koi Fin Wave
          ctx.beginPath();
          ctx.moveTo(-6, 0);
          ctx.lineTo(-24 + Math.sin(koiAngle * 8) * 6, -10);
          ctx.lineTo(-24 + Math.sin(koiAngle * 8) * 6, 10);
          ctx.closePath();
          ctx.fillStyle = "rgba(255, 255, 255, 0.7)";
          ctx.fill();

          ctx.restore();
        });

        animId = requestAnimationFrame(renderKoi);
      };
      renderKoi();
    }

    // 16. SAKURA BLOSSOM PETALS
    else if (type === "bundle_sakura_blossom" || type === "sakura_blossom") {
      const petals = Array.from({ length: 70 }).map(() => ({
        x: Math.random() * width,
        y: Math.random() * height,
        size: Math.random() * 8 + 4,
        rot: Math.random() * Math.PI,
        vRot: (Math.random() - 0.5) * 0.05,
        vx: Math.random() * 1.5 + 0.8,
        vy: Math.random() * 1.2 + 0.6,
        alpha: Math.random() * 0.8 + 0.2
      }));

      const renderSakura = () => {
        ctx.fillStyle = "rgba(18, 8, 20, 0.25)";
        ctx.fillRect(0, 0, width, height);

        petals.forEach((p) => {
          p.rot += p.vRot;
          p.x += p.vx + Math.sin(p.rot) * 0.5;
          p.y += p.vy;

          if (p.y > height) { p.y = -10; p.x = Math.random() * width; }
          if (p.x > width) { p.x = -10; }

          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rot);
          ctx.beginPath();
          ctx.ellipse(0, 0, p.size, p.size * 0.55, 0, 0, Math.PI * 2);
          ctx.fillStyle = "#f472b6";
          ctx.shadowBlur = 12;
          ctx.shadowColor = "#ec4899";
          ctx.globalAlpha = p.alpha;
          ctx.fill();
          ctx.restore();
        });

        animId = requestAnimationFrame(renderSakura);
      };
      renderSakura();
    }

    // 17. DRAGON LORE FIRE & SCALES
    else if (type === "dragon_lore") {
      let t = 0;
      const sparks = Array.from({ length: 80 }).map(() => ({
        x: Math.random() * width,
        y: Math.random() * height,
        r: Math.random() * 3 + 1,
        vx: (Math.random() - 0.5) * 1.5,
        vy: -(Math.random() * 2.5 + 1.2),
        color: Math.random() > 0.4 ? "#fbbf24" : "#ef4444"
      }));

      const renderDragonLore = () => {
        t += 0.02;
        ctx.fillStyle = "rgba(18, 5, 2, 0.25)";
        ctx.fillRect(0, 0, width, height);

        // Dragon Breath Wave
        const grad = ctx.createRadialGradient(width / 2, height * 0.8, 20, width / 2, height * 0.8, width * 0.6);
        grad.addColorStop(0, "rgba(245, 158, 11, 0.2)");
        grad.addColorStop(0.5, "rgba(220, 38, 38, 0.1)");
        grad.addColorStop(1, "transparent");
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, width, height);

        sparks.forEach((s) => {
          ctx.save();
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
          ctx.fillStyle = s.color;
          ctx.shadowBlur = 14;
          ctx.shadowColor = s.color;
          ctx.fill();
          ctx.restore();

          s.x += s.vx + Math.sin(t * 2) * 0.5;
          s.y += s.vy;
          if (s.y < 0) { s.y = height + 10; s.x = Math.random() * width; }
        });
        animId = requestAnimationFrame(renderDragonLore);
      };
      renderDragonLore();
    }

    // 18. VALORANT RADIANT CRYSTALS
    else if (type === "valorant_radiant") {
      let t = 0;
      const crystals = Array.from({ length: 45 }).map(() => ({
        x: Math.random() * width,
        y: Math.random() * height,
        size: Math.random() * 12 + 6,
        rot: Math.random() * Math.PI,
        vRot: (Math.random() - 0.5) * 0.03,
        color: Math.random() > 0.5 ? "#00e5ff" : "#a855f7"
      }));

      const renderRadiant = () => {
        t += 0.015;
        ctx.fillStyle = "rgba(4, 8, 22, 0.25)";
        ctx.fillRect(0, 0, width, height);

        crystals.forEach((c) => {
          c.rot += c.vRot;
          ctx.save();
          ctx.translate(c.x, c.y);
          ctx.rotate(c.rot);
          ctx.beginPath();
          ctx.moveTo(0, -c.size);
          ctx.lineTo(c.size * 0.6, 0);
          ctx.lineTo(0, c.size);
          ctx.lineTo(-c.size * 0.6, 0);
          ctx.closePath();
          ctx.strokeStyle = c.color;
          ctx.shadowBlur = 12;
          ctx.shadowColor = c.color;
          ctx.lineWidth = 1.5;
          ctx.stroke();
          ctx.restore();
        });
        animId = requestAnimationFrame(renderRadiant);
      };
      renderRadiant();
    }

    // 19. DARK SOULS BONFIRE EMBERS
    else if (type === "dark_souls_bonfire") {
      const embers = Array.from({ length: 70 }).map(() => ({
        x: width / 2 + (Math.random() - 0.5) * 200,
        y: height * 0.8 + (Math.random() - 0.5) * 100,
        r: Math.random() * 2.5 + 0.8,
        vy: -(Math.random() * 2 + 0.8),
        vx: (Math.random() - 0.5) * 1.2,
        alpha: Math.random() * 0.9 + 0.1
      }));

      const renderBonfire = () => {
        ctx.fillStyle = "rgba(8, 5, 5, 0.25)";
        ctx.fillRect(0, 0, width, height);

        // Bonfire Warm Light
        const grad = ctx.createRadialGradient(width / 2, height * 0.8, 10, width / 2, height * 0.8, 300);
        grad.addColorStop(0, "rgba(249, 115, 22, 0.35)");
        grad.addColorStop(0.5, "rgba(185, 28, 28, 0.15)");
        grad.addColorStop(1, "transparent");
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, width, height);

        embers.forEach((e) => {
          ctx.save();
          ctx.beginPath();
          ctx.arc(e.x, e.y, e.r, 0, Math.PI * 2);
          ctx.fillStyle = "#fb923c";
          ctx.shadowBlur = 15;
          ctx.shadowColor = "#ea580c";
          ctx.globalAlpha = e.alpha;
          ctx.fill();
          ctx.restore();

          e.x += e.vx;
          e.y += e.vy;
          e.alpha -= 0.003;

          if (e.y < height * 0.2 || e.alpha <= 0) {
            e.x = width / 2 + (Math.random() - 0.5) * 200;
            e.y = height * 0.8 + (Math.random() - 0.5) * 50;
            e.alpha = Math.random() * 0.9 + 0.1;
          }
        });
        animId = requestAnimationFrame(renderBonfire);
      };
      renderBonfire();
    }

    // 20. CYBERPUNK EDGERUNNERS TRAIL
    else if (type === "cyberpunk_edgerunners") {
      let phase = 0;
      const trails = Array.from({ length: 40 }).map(() => ({
        x: Math.random() * width,
        y: Math.random() * height,
        len: Math.random() * 80 + 30,
        speed: Math.random() * 6 + 4,
        color: Math.random() > 0.5 ? "#ff0055" : "#00f0ff"
      }));

      const renderEdgerunners = () => {
        phase += 0.05;
        ctx.fillStyle = "rgba(5, 5, 8, 0.25)";
        ctx.fillRect(0, 0, width, height);

        trails.forEach((t) => {
          ctx.save();
          ctx.strokeStyle = t.color;
          ctx.lineWidth = 2;
          ctx.shadowBlur = 12;
          ctx.shadowColor = t.color;
          ctx.beginPath();
          ctx.moveTo(t.x, t.y);
          ctx.lineTo(t.x + t.len, t.y);
          ctx.stroke();
          ctx.restore();

          t.x += t.speed;
          if (t.x > width) {
            t.x = -t.len;
            t.y = Math.random() * height;
          }
        });
        animId = requestAnimationFrame(renderEdgerunners);
      };
      renderEdgerunners();
    }

    // 21. MINECRAFT NETHER PORTAL
    else if (type === "nether_portal") {
      let t = 0;
      const blocks = Array.from({ length: 60 }).map(() => ({
        x: Math.random() * width,
        y: Math.random() * height,
        size: Math.random() * 14 + 6,
        vy: -(Math.random() * 1.5 + 0.5),
        color: Math.random() > 0.5 ? "#a855f7" : "#c084fc"
      }));

      const renderNether = () => {
        t += 0.02;
        ctx.fillStyle = "rgba(6, 2, 14, 0.25)";
        ctx.fillRect(0, 0, width, height);

        blocks.forEach((b) => {
          ctx.save();
          ctx.fillStyle = b.color;
          ctx.shadowBlur = 14;
          ctx.shadowColor = b.color;
          ctx.fillRect(b.x, b.y, b.size, b.size);
          ctx.restore();

          b.y += b.vy;
          if (b.y < -20) { b.y = height + 20; b.x = Math.random() * width; }
        });
        animId = requestAnimationFrame(renderNether);
      };
      renderNether();
    }

    // 22. ARCANE HEXTECH MAGIC
    else if (type === "arcane_hextech") {
      let t = 0;
      const sparks = Array.from({ length: 50 }).map(() => ({
        angle: Math.random() * Math.PI * 2,
        dist: Math.random() * (width * 0.4),
        speed: Math.random() * 1.5 + 0.5,
        color: Math.random() > 0.5 ? "#00e5ff" : "#6366f1"
      }));

      const renderArcane = () => {
        t += 0.02;
        ctx.fillStyle = "rgba(2, 6, 23, 0.25)";
        ctx.fillRect(0, 0, width, height);

        const cx = width / 2;
        const cy = height / 2;

        // Rune circle
        ctx.save();
        ctx.strokeStyle = "rgba(0, 229, 255, 0.3)";
        ctx.lineWidth = 2;
        ctx.shadowBlur = 15;
        ctx.shadowColor = "#00e5ff";
        ctx.beginPath();
        ctx.arc(cx, cy, 120 + Math.sin(t) * 10, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();

        sparks.forEach((p) => {
          p.dist += p.speed;
          if (p.dist > width * 0.5) p.dist = 20;
          const x = cx + Math.cos(p.angle + t) * p.dist;
          const y = cy + Math.sin(p.angle + t) * p.dist;

          ctx.save();
          ctx.beginPath();
          ctx.arc(x, y, 2.5, 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.shadowBlur = 10;
          ctx.shadowColor = p.color;
          ctx.fill();
          ctx.restore();
        });
        animId = requestAnimationFrame(renderArcane);
      };
      renderArcane();
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      if (animId) cancelAnimationFrame(animId);
    };
  }, [type, customUrl]);

  if (type === "none" && !customUrl) return null;

  const isDirectVideo = customUrl || type === "anime_demon_eyes" || type === "cyber_samurai_slash" || (typeof type === "string" && (type.endsWith(".mp4") || type.endsWith(".webm") || type.startsWith("/videos/")));
  const resolvedVideoSrc = customUrl || (type === "anime_demon_eyes" ? "/videos/anime_demon_eyes.mp4" : (type === "cyber_samurai_slash" ? "/videos/cyber_samurai_slash.mp4" : type));

  if (isDirectVideo) {
    return (
      <div className={`fixed inset-0 overflow-hidden pointer-events-none z-0 ${className}`}>
        <video
          key={resolvedVideoSrc}
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover opacity-85 scale-105 filter saturate-125 brightness-105"
          src={resolvedVideoSrc}
        />
      </div>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      className={`fixed inset-0 pointer-events-none z-0 opacity-80 ${className}`}
      style={{ display: "block" }}
    />
  );
}
